<template>
  <asc-tooltip :tooltip="getToolTip" bottom :tooltip-length="0">
    <div
      :class="[
        `asc-plot-data-tab--container ${active ? 'active' : ''}`,
        disabled ? 'asc-plot-data-tab--disabled' : '',
      ]"
      @click="handleTabClick"
      @mouseover="handleHoverStyle"
      @mouseleave="handleResetStyle"
    >
      <template v-if="!saving">
        <asc-icon
          class="asc-plot-data-tab--container__icon"
          :color="getTabIconColor"
          :icon="getIcon"
          :size="16"
        />
      </template>
      <asc-tooltip
        :tooltip="getTabTooltip"
        bottom
        is-icon-tooltip
        :max-width="'325'"
      >
        <v-clamp
          class="asc-plot-data-tab--container__text"
          autoresize
          :max-lines="1"
        >
          {{ title }}
        </v-clamp>
      </asc-tooltip>
      <template v-if="error">
        <v-icon
          class="asc-plot-data-tab--container__icon"
          :color="getErrorIconColor"
          size="18"
          >mdi-alert-circle</v-icon
        >
      </template>
      <template v-if="isTabMenuDisabled ? false : !saving">
        <asc-menu-container :icon-color="menuIconColor" small>
          <asc-menu-list
            class="asc-plot-data-tab--menu-list"
            dense
            :primary-items="getMenuItems"
            :width="101"
            @menuSelection="handleMenuSelection"
          />
        </asc-menu-container>
      </template>
    </div>
  </asc-tooltip>
</template>

<script>
import VClamp from 'vue-clamp'
import AscIcon from '@/components/atoms/AscIcon'
import AscMenuContainer from '@/components/atoms/AscMenuContainer'
import AscMenuList from '@/components/atoms/AscMenuList'
import AscTooltip from '@/components/atoms/AscTooltip'
import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscPlotDataTab',

  components: {
    VClamp,
    AscIcon,
    AscMenuContainer,
    AscMenuList,
    AscTooltip,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'square',
    },
    iconColor: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    numberTabs: {
      type: Number,
      default: 0,
    },
    numberVisibleTabs: {
      type: Number,
      default: 0,
    },
    tabIndex: {
      type: Number,
      default: -1,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    isTabMenuDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      menuIconColor: null,
    }
  },

  computed: {
    getToolTip() {
      return this.disabled ? 'Adjust Global Filter settings to edit.' : ''
    },

    getTabTooltip() {
      return this.disabled ? '' : this.title
    },

    getMenuItems() {
      const menuItems = [
        {
          isMenuItem: true,
          title: this.hidden ? 'Show' : 'Hide',
          disabled: false,
        },
        {
          isMenuItem: true,
          title: 'Remove',
          disabled: this.isRemoveMenuDisabled,
        },
      ]

      return menuItems
    },

    isRemoveMenuDisabled() {
      if (this.readonly) {
        return true
      }

      return !this.hidden && (this.numberTabs < 2 || this.numberVisibleTabs < 2)
    },

    getIcon() {
      return this.hidden ? 'eyeOff' : this.icon
    },

    getTabIconColor() {
      return this.hidden ? variables.gray900 : this.iconColor
    },

    getErrorIconColor() {
      return variables.warningbase
    },

    getIconColor() {
      return variables.accent500
    },
  },

  methods: {
    handleHoverStyle() {
      this.menuIconColor = variables.accent500
    },

    handleResetStyle() {
      this.menuIconColor = null
    },

    handleMenuSelection(selection) {
      if (selection === 'Hide' || selection === 'Show') {
        this.$emit('updateTabView', { selection, tabIndex: this.tabIndex })
      } else {
        this.$emit('remove', this.tabIndex)
      }
    },

    handleTabClick(event) {
      if (this.disabled) return
      if (event) event.stopPropagation()
      this.$emit('click')
    },
  },
}
</script>

<style lang="scss">
.asc-plot-data-tab {
  &--disabled {
    cursor: not-allowed !important;
  }

  &--container {
    align-items: center;
    background-color: $gray-200;
    display: flex;
    flex-flow: row nowrap;
    padding: 8px;
    max-width: 217px;
    border-right: 1px solid $primary-white;

    &:hover {
      background-color: $gray-100;
      cursor: pointer;
    }

    &:hover > .asc-tooltip--container > &__text {
      color: $accent-700;
    }

    &.active {
      background-color: $primary-white;
      border-bottom: 2px solid $accent-400;

      &:hover {
        background-color: $primary-white;
      }

      &:hover > .asc-tooltip--container > .asc-plot-data-tab--container__text {
        color: $primary-black;
      }
    }

    &__icon {
      margin-right: 8px;
    }

    &__text {
      color: $primary-black;
      font-size: $font-size-md;
      line-height: 18px;
      margin-right: 8px;
      width: 128px;
      text-transform: uppercase;
    }
  }

  &--menu-list.asc-menu-list--container {
    .v-list--dense {
      .v-list-item {
        max-height: 34px;
        padding: 8px 6px;
      }
    }
  }
}
</style>
